/**
 * 組織階層レベル設定 API クライアント
 *
 * `organization_level` テーブル（テナント:1=1:1）の使用フラグを取得する。
 *
 * # 列と organization.level の対応
 *   - first  ... tenth → organization.level = 0 ... 9
 *   - store            → organization.level = 11
 *   - department       → organization.level = 12
 *   - section          → organization.level = 13
 */

import { authFetch, API_BASE_URL } from "./client";

/** 組織階層レベル使用フラグ（bool 化済み） */
export interface OrganizationLevelResponse {
    id: number;
    tenant_id: string;
    first: boolean;
    second: boolean;
    third: boolean;
    fourth: boolean;
    fifth: boolean;
    sixth: boolean;
    seventh: boolean;
    eighth: boolean;
    ninth: boolean;
    tenth: boolean;
    store: boolean;
    department: boolean;
    section: boolean;
}

/** 認証ユーザーの所属テナントの組織階層使用フラグを取得 */
export async function fetchMyOrganizationLevel(): Promise<OrganizationLevelResponse> {
    const response = await authFetch(`${API_BASE_URL}/organization-levels/me`);
    if (!response.ok) {
        throw new Error(`組織階層設定の取得に失敗しました: ${response.status}`);
    }
    return response.json();
}

/** organization_level の bool フラグキー（first..tenth, store, department, section） */
export type OrganizationLevelFlagKey =
    | "first" | "second" | "third" | "fourth" | "fifth"
    | "sixth" | "seventh" | "eighth" | "ninth" | "tenth"
    | "store" | "department" | "section";

/**
 * フラグキー → organization.level の数値マッピング
 *
 * UI 側で各「アクティブな階層行」がどの level の組織を引っ張ってくるかを決める。
 */
export const LEVEL_FLAG_TO_ORG_LEVEL: Record<OrganizationLevelFlagKey, number> = {
    first: 0, second: 1, third: 2, fourth: 3, fifth: 4,
    sixth: 5, seventh: 6, eighth: 7, ninth: 8, tenth: 9,
    store: 11, department: 12, section: 13,
};

/** UI に表示するラベル */
export const LEVEL_FLAG_LABEL: Record<OrganizationLevelFlagKey, string> = {
    first: "第1組織階層", second: "第2組織階層", third: "第3組織階層",
    fourth: "第4組織階層", fifth: "第5組織階層", sixth: "第6組織階層",
    seventh: "第7組織階層", eighth: "第8組織階層", ninth: "第9組織階層",
    tenth: "第10組織階層",
    store: "店舗", department: "部門", section: "課",
};

/** OrganizationLevel 行構築の順序（描画順） */
export const LEVEL_FLAG_ORDER: readonly OrganizationLevelFlagKey[] = [
    "first", "second", "third", "fourth", "fifth",
    "sixth", "seventh", "eighth", "ninth", "tenth",
    "store", "department", "section",
] as const;
